import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, ACCOUNT_COLORS } from "@/lib/api";
import { getActiveLeagueId, setActiveLeagueId } from "@/lib/league";
import { toast } from "sonner";
import UserEditor from "@/components/UserEditor";
import LockConfirmModal from "@/components/LockConfirmModal";
import BrandLogo from "@/components/BrandLogo";
import OTPModal from "@/components/OTPModal";
import AdminPurgeUtility from "@/components/AdminPurgeUtility";

const TROPHY = "https://static.prod-images.emergentagent.com/jobs/1b0b3fd3-e19a-43a9-907c-68e9e32f9aea/images/3dceae09e59e2fecf11ccb3e586ca15ed7cd346b32adaa375ab18d6ccbb2171b.png";

// Phase 20.16 — Rogue Directive settings-page status line. Never shows the
// actual drop time (that's the whole point of "unannounced") — just
// whether the weekly automation is on track.
const ROGUE_STATE_LABEL = {
  not_active: "LEAGUE NOT ACTIVE — AUTOMATIONS PAUSED",
  pending_schedule: "AWAITING SUNDAY 23:00 SCHEDULING",
  scheduled: "QUEUED FOR THIS WEEK",
  deployed: "DEPLOYED THIS WEEK",
  resolved: "RESOLVED FOR THIS WEEK",
};
const ROGUE_STATE_COLOR = {
  not_active: "var(--stakes-muted)",
  pending_schedule: "var(--stakes-muted)",
  scheduled: "var(--neon-blue)",
  deployed: "var(--stakes-child)",
  resolved: "var(--stakes-muted)",
};

export default function SetupScreen() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const forceNew = searchParams.get("new") === "1";
  const [loading, setLoading] = useState(true);
  const [league, setLeague] = useState(null);
  const [users, setUsers] = useState([]);
  const [showLock, setShowLock] = useState(false);
  const [clunk, setClunk] = useState(false);

  // Admin Control Panel — entry gate
  const [verified, setVerified] = useState(false);
  const [adminActorId, setAdminActorId] = useState(null);
  const [gatePin, setGatePin] = useState("");
  const [gateError, setGateError] = useState("");
  const [gateBusy, setGateBusy] = useState(false);

  // Draft form (used until league is created)
  const [leagueName, setLeagueName] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminPin, setAdminPin] = useState("");
  const [prize, setPrize] = useState("");

  // V2 — OTP gate state
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpMode, setOtpMode] = useState("create"); // "create" | "recover" | "set-recovery"
  const [otpLockedEmail, setOtpLockedEmail] = useState(null);
  const [otpPendingEmail, setOtpPendingEmail] = useState(""); // unverified email captured before OTP for the set-recovery flow
  const [recoveryStage, setRecoveryStage] = useState(null); // null | "set-new-pin"
  const [recoveryToken, setRecoveryToken] = useState("");
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoveryNewPin, setRecoveryNewPin] = useState("");
  const [recoveryEmailInput, setRecoveryEmailInput] = useState(""); // legacy-league recovery-email card

  // V2 — treat the Phase 16 backfill placeholder as effectively-null so
  // legacy leagues that received the dummy email still surface the recovery
  // card AND block the Forgot-PIN flow (which would otherwise try to email
  // a fake address that nobody owns).
  const PLACEHOLDER_OWNER = "admin@stakesleague.com";
  const realOwnerEmail = (() => {
    const e = ((league?.owner_email) || "").trim().toLowerCase();
    if (!e || e === PLACEHOLDER_OWNER) return null;
    return league.owner_email; // preserve original casing
  })();
  const hasRealOwnerEmail = !!realOwnerEmail;

  // New player form
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("member");
  const [newPin, setNewPin] = useState("");

  useEffect(() => {
    (async () => {
      const id = forceNew ? null : getActiveLeagueId();
      try {
        const l = id ? await api.getLeagueById(id) : null;
        if (l) {
          setLeague(l);
          setLeagueName(l.name);
          setAdminName(l.admin_name);
          setPrize(l.monthly_prize || "");
          const u = await api.listUsers(l.id);
          setUsers(u);
        }
      } catch { /* league no longer exists */ }
      setLoading(false);
    })();
  }, [forceNew]);

  // No gate needed when creating a fresh league via /setup?new=1
  // or when no existing league was loaded. Decided ONLY after initial load
  // completes — otherwise `league` is still null mid-fetch and we'd skip the gate.
  useEffect(() => {
    if (loading) return;
    if (forceNew || !league) setVerified(true);
  }, [loading, forceNew, league]);

  const verifyGate = async (e) => {
    e?.preventDefault?.();
    if (gatePin.length !== 4) {
      setGateError("4 DIGITS REQUIRED");
      return;
    }
    // Security backlog Phase A — verified server-side now (issues a real
    // session token) instead of matching the typed PIN against the
    // already-loaded `users` list in the browser.
    setGateBusy(true);
    try {
      const r = await api.verifyAdminPin(league.id, gatePin);
      if (!r.ok) {
        setGateError("INCORRECT ADMIN PIN");
        setGatePin("");
        return;
      }
      setAdminActorId(r.admin_user_id);
      setVerified(true);
      setGatePin("");
      setGateError("");
    } catch {
      setGateError("COULDN'T VERIFY — TRY AGAIN");
    } finally {
      setGateBusy(false);
    }
  };

  const locked = league?.status === "active";

  // Phase 20.16 — Rogue Directive status line (Settings · System Protocols).
  const [rogueStatus, setRogueStatus] = useState(null);
  useEffect(() => {
    if (!league?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.getRogueStatus(league.id);
        if (!cancelled) setRogueStatus(r);
      } catch { /* keep last known state on a failed fetch */ }
    })();
    return () => { cancelled = true; };
  }, [league?.id, league?.status]);
  const rogueState = rogueStatus?.state;

  const createLeague = async () => {
    if (!leagueName.trim() || !adminName.trim()) {
      toast.error("League name and admin name required");
      return;
    }
    if (!adminPin || adminPin.length !== 4) {
      toast.error("Admin needs a 4-digit PIN");
      return;
    }
    // V2: Require email verification BEFORE the league row hits Mongo so
    // every new league is permanently tied to a verified owner email.
    setOtpMode("create");
    setOtpLockedEmail(null);
    setOtpOpen(true);
  };

  const finalizeLeagueCreation = async (verifiedEmail) => {
    try {
      const l = await api.createLeague({
        name: leagueName.trim(),
        admin_name: adminName.trim(),
        monthly_prize: prize.trim(),
        owner_email: verifiedEmail,
        timezone: (() => {
          try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; }
        })(),
      });
      setLeague(l);
      setActiveLeagueId(l.id);
      // Auto-create the admin as the first user with PIN
      const adminUser = await api.createUser({
        league_id: l.id,
        name: adminName.trim(),
        account_type: "admin",
        pin: adminPin,
      });
      setUsers([adminUser]);
      toast.success("League drafted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create league");
    }
  };

  const handleOtpVerified = async ({ email, verification_token }) => {
    setOtpOpen(false);
    if (otpMode === "create") {
      // Verified email becomes the league's owner_email of record
      await finalizeLeagueCreation(email);
      return;
    }
    if (otpMode === "recover") {
      // Hold the token + email and switch UI into "SET NEW ADMIN PIN" mode
      setRecoveryEmail(email);
      setRecoveryToken(verification_token);
      setRecoveryStage("set-new-pin");
      setRecoveryNewPin("");
      return;
    }
    if (otpMode === "set-recovery") {
      // Legacy-league one-time attach
      try {
        const updated = await api.setRecoveryEmail(league.id, email, verification_token);
        setLeague(updated);
        setRecoveryEmailInput("");
        toast.success(`Recovery email locked to ${email}`);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Couldn't attach recovery email");
      }
    }
  };

  const startSetRecoveryEmailFlow = (rawEmail) => {
    const email = (rawEmail || "").trim().toLowerCase();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Enter a valid email first");
      return;
    }
    setOtpMode("set-recovery");
    setOtpLockedEmail(email);
    setOtpPendingEmail(email);
    setOtpOpen(true);
  };

  const startRecoveryFlow = () => {
    if (!hasRealOwnerEmail) {
      toast.error("This league has no recovery email on file");
      return;
    }
    setOtpMode("recover");
    setOtpLockedEmail(realOwnerEmail);
    setOtpOpen(true);
  };

  const submitNewAdminPin = async (e) => {
    e?.preventDefault?.();
    if (!recoveryNewPin || recoveryNewPin.length !== 4) {
      toast.error("PIN must be 4 digits");
      return;
    }
    try {
      await api.resetAdminPin(league.id, recoveryEmail, recoveryToken, recoveryNewPin);
      // Reload the roster so the gate's local admin list reflects the new PIN
      const u = await api.listUsers(league.id);
      setUsers(u);
      const admin = u.find((x) => x.account_type === "admin");
      if (admin) setAdminActorId(admin.id);
      setVerified(true);
      setRecoveryStage(null);
      setRecoveryToken("");
      setRecoveryEmail("");
      setRecoveryNewPin("");
      toast.success("Admin PIN reset — control panel unlocked");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't reset PIN");
    }
  };

  const saveLeagueMeta = async () => {
    if (!league || locked) return;
    try {
      const updated = await api.updateLeague(league.id, {
        name: leagueName,
        admin_name: adminName,
        monthly_prize: prize,
      });
      setLeague(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  // Phase 17 — timezone calibration (allowed mid-league per server.py override)
  const saveTimezone = async (tz) => {
    if (!league || !tz) return;
    try {
      const updated = await api.updateLeague(league.id, { timezone: tz });
      setLeague(updated);
      toast.success(`Timezone set to ${tz}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Timezone update failed");
    }
  };

  const detectDeviceTimezone = () => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return "UTC";
    }
  };

  const addUser = async () => {
    if (!newName.trim() || !league) return;
    // V2: PIN optional at creation. Empty = the player will "claim" their
    // own PIN on first login. If a PIN IS typed, it must be 4 digits.
    const trimmedPin = (newPin || "").trim();
    if (trimmedPin && (trimmedPin.length !== 4 || !/^\d{4}$/.test(trimmedPin))) {
      toast.error("PIN must be 4 digits (or leave blank for self-claim)");
      return;
    }
    try {
      const u = await api.createUser({
        league_id: league.id,
        name: newName.trim(),
        account_type: newType,
        pin: trimmedPin || null,
      });
      setUsers((arr) => [...arr, u]);
      setNewName(""); setNewType("member"); setNewPin("");
      toast.success(trimmedPin ? `${u.name} added to the league` : `${u.name} added — they'll claim their PIN on first login`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add player");
    }
  };

  const onUserUpdate = (u) => setUsers((arr) => arr.map((x) => x.id === u.id ? u : x));
  const onUserDelete = async (id) => {
    try {
      await api.deleteUser(id);
      setUsers((arr) => arr.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove");
    }
  };

  const startLeague = async () => {
    if (!league) return;
    try {
      const l = await api.startLeague(league.id);
      setLeague(l);
      setShowLock(false);
      setClunk(true);
      setTimeout(() => setClunk(false), 800);
      toast.success("LEAGUE LOCKED. STAKES ARE LIVE.");
      setTimeout(() => nav("/"), 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't lock league");
    }
  };

  const resetAll = async () => {
    if (!league) return;
    if (!window.confirm("Wipe this league and start over? This deletes all players and rules.")) return;
    await api.resetLeague(league.id);
    setLeague(null);
    setActiveLeagueId(null);
    setUsers([]);
    setLeagueName(""); setAdminName(""); setAdminPin(""); setPrize("");
    nav("/welcome");
  };

  const toggleStatus = async () => {
    if (!league) return;
    try {
      const isActive = league.status === "active";
      const l = isActive
        ? await api.unlockLeague(league.id)
        : await api.startLeague(league.id);
      setLeague(l);
      toast.success(
        isActive
          ? "SWITCHED TO DRAFT · RULES EDITABLE"
          : "SWITCHED TO ACTIVE · STAKES ARE LIVE",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't switch status");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="label-mono">LOADING SETUP…</div>
      </div>
    );
  }

  // ============= ADMIN CONTROL PANEL GATE =============
  // Existing league + not creating new + not yet verified → require Admin PIN
  if (league && !forceNew && !verified) {
    // Recovery sub-stage: OTP already verified, now collect the new PIN
    if (recoveryStage === "set-new-pin") {
      return (
        <div className="min-h-screen flex items-center justify-center px-4">
          <form
            onSubmit={submitNewAdminPin}
            className="w-full max-w-md border-2 border-[var(--neon-pink)] bg-[var(--stakes-surface)] p-6 md:p-8"
            style={{ boxShadow: "0 0 32px rgba(255,20,147,0.22), inset 0 0 60px rgba(255,20,147,0.04)" }}
            data-testid="admin-set-new-pin-form"
          >
            <div className="label-mono" style={{ color: "var(--neon-pink)" }}>EMAIL VERIFIED</div>
            <h1 className="font-anton text-4xl md:text-5xl uppercase leading-none mt-2">
              Set New<br/>Admin PIN
            </h1>
            <div className="label-mono mt-3 text-[var(--stakes-muted)]">
              CHOOSE A FRESH 4-DIGIT PIN. THIS REPLACES THE OLD ADMIN PIN.
            </div>
            <div className="mt-6">
              <div className="label-mono mb-1">NEW ADMIN PIN</div>
              <input
                autoFocus
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={recoveryNewPin}
                onChange={(e) => setRecoveryNewPin(e.target.value.replace(/\D/g,"").slice(0,4))}
                placeholder="----"
                autoComplete="new-password"
                className="w-full bg-transparent border-b-2 border-[var(--stakes-border)] focus:border-[var(--neon-pink)] outline-none font-anton text-5xl text-center tracking-[0.4em] pb-2"
                data-testid="admin-new-pin-input"
              />
            </div>
            <div className="mt-6 flex gap-2">
              <button
                type="button"
                onClick={() => { setRecoveryStage(null); setRecoveryToken(""); setRecoveryEmail(""); setRecoveryNewPin(""); }}
                className="flex-1 h-12 label-mono border-2 border-[var(--stakes-border)] hover:bg-white hover:!text-black hover-flip"
                data-testid="admin-recovery-cancel"
              >
                ← BACK
              </button>
              <button
                type="submit"
                disabled={recoveryNewPin.length !== 4}
                className="flex-1 h-12 bg-[var(--neon-pink)] !text-black font-mono font-black uppercase tracking-wider border-2 border-[var(--neon-pink)] hover:bg-black hover:!text-white hover:border-white hover-flip disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="admin-set-pin-submit"
              >
                SAVE & UNLOCK →
              </button>
            </div>
          </form>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <form
          onSubmit={verifyGate}
          className="w-full max-w-md border-2 border-[var(--stakes-admin)] bg-[var(--stakes-surface)] p-6 md:p-8"
          style={{ boxShadow: "0 0 32px rgba(255,214,10,0.18), inset 0 0 60px rgba(255,214,10,0.05)" }}
          data-testid="setup-admin-gate"
        >
          <div className="label-mono" style={{ color: "var(--stakes-admin)" }}>ADMIN CONTROL PANEL</div>
          <h1 className="font-anton text-4xl md:text-5xl uppercase leading-none mt-2">
            Restricted<br/>Access
          </h1>
          <div className="label-mono mt-4 text-[var(--stakes-muted)]">
            ENTER ADMIN PIN TO MANAGE PLAYERS, RULES, AND REDEMPTION PENALTIES.
          </div>
          <div className="mt-6">
            <div className="label-mono mb-1">ADMIN PIN</div>
            <input
              autoFocus
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={gatePin}
              onChange={(e) => { setGatePin(e.target.value.replace(/\D/g,"").slice(0,4)); setGateError(""); }}
              placeholder="----"
              autoComplete="off"
              className="w-full bg-transparent border-b-2 border-[var(--stakes-border)] focus:border-[var(--stakes-admin)] outline-none font-anton text-5xl text-center tracking-[0.4em] pb-2"
              data-testid="setup-gate-pin-input"
            />
            {gateError && (
              <div className="mt-3 label-mono" style={{ color: "var(--stakes-primary)" }} data-testid="setup-gate-error">
                {gateError}
              </div>
            )}
          </div>

          {/* V2 — Forgot PIN? */}
          <div className="mt-4 flex items-center justify-between">
            <button
              type="button"
              onClick={startRecoveryFlow}
              disabled={!hasRealOwnerEmail}
              className="label-mono underline underline-offset-2 hover:text-[var(--neon-blue)] disabled:opacity-30 disabled:cursor-not-allowed disabled:no-underline"
              data-testid="setup-gate-forgot-pin"
              title={hasRealOwnerEmail ? `Send recovery code to ${realOwnerEmail}` : "No recovery email on file for this league"}
            >
              ↺ FORGOT PIN?
            </button>
            {hasRealOwnerEmail && (
              <span className="label-mono text-[var(--stakes-muted)] truncate ml-2" data-testid="setup-gate-owner-email" title={realOwnerEmail}>
                {realOwnerEmail}
              </span>
            )}
          </div>

          <div className="mt-6 flex gap-2">
            <button
              type="button"
              onClick={() => nav("/")}
              className="flex-1 h-12 label-mono border border-[var(--stakes-border)] hover:bg-white hover:text-black hover-flip"
              data-testid="setup-gate-cancel"
            >
              ← BACK
            </button>
            <button
              type="submit"
              disabled={gatePin.length !== 4 || gateBusy}
              className="flex-1 h-12 font-barlow font-black uppercase tracking-wider bg-[var(--stakes-admin)] text-black disabled:opacity-30 disabled:cursor-not-allowed hover-flip"
              data-testid="setup-gate-submit"
            >
              {gateBusy ? "VERIFYING…" : "UNLOCK →"}
            </button>
          </div>
        </form>

        {/* OTP modal lives at gate level for the recover flow */}
        <OTPModal
          open={otpOpen}
          onClose={() => setOtpOpen(false)}
          onVerified={handleOtpVerified}
          title="ADMIN PIN RECOVERY"
          subtitle={`A 6-DIGIT CODE WILL BE EMAILED TO ${(realOwnerEmail || "").toUpperCase()}.`}
          lockedEmail={realOwnerEmail}
          ctaSendLabel="EMAIL CODE"
          ctaVerifyLabel="VERIFY →"
        />
      </div>
    );
  }

  return (
    <div
      className={`h-[100dvh] flex flex-col overflow-hidden ${clunk ? "animate-clunk" : ""}`}
      data-testid="setup-shell"
    >
      {/* Header — non-shrinking flex child (replaces window-scroll sticky) */}
      <header className="flex-shrink-0 z-40 backdrop-blur-2xl bg-black/60 border-b border-white/10 safe-top">
        <div className="max-w-[1600px] mx-auto px-4 md:px-12 py-3 md:py-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <BrandLogo size="sm" />
            <span className="label-mono">/ SETUP</span>
          </div>
          <div className="flex items-center gap-2">
            {league?.code && (
              <span
                className="label-mono px-3 h-9 hidden sm:inline-flex items-center border border-[var(--stakes-border)]"
                data-testid="setup-header-code"
                title="League join code"
              >
                # {league.code}
              </span>
            )}
            {league && (
              <>
                <span
                  className="label-mono px-3 py-1 border"
                  style={{
                    borderColor: locked ? "var(--stakes-primary)" : "var(--stakes-border)",
                    color: locked ? "var(--stakes-primary)" : "var(--stakes-muted)",
                  }}
                  data-testid="league-status-badge"
                >
                  ● {locked ? "ACTIVE" : "DRAFT"}
                </span>
                <button
                  onClick={() => nav("/")}
                  className="label-mono px-3 h-9 border border-[var(--stakes-border)] hover:bg-white hover:text-black hover-flip"
                  data-testid="header-home-button"
                >
                  HOME →
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Scrollable viewport — flex-1 + overflow-y-auto. The Setup screen has
          no fixed BottomNav, but we still apply mobile-bottom-pad so the iOS
          home indicator never overlaps the last setting card. */}
      <div
        className="flex-1 overflow-y-auto mobile-bottom-pad"
        data-testid="setup-scroll-area"
      >
      <main className="max-w-[1600px] mx-auto px-8 lg:px-12 py-10 lg:py-16">
        {!league ? (
          /* ---------- League creation ---------- */
          <section className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5 animate-slide-up">
              <button
                onClick={() => nav("/welcome")}
                className="label-mono px-3 h-10 border border-[var(--stakes-border)] hover:border-white hover:bg-white hover:!text-black hover-flip mb-6"
                data-testid="setup-return-to-login-button"
                title="Discard draft and go back to login"
              >
                ← RETURN TO LOGIN
              </button>
              <div className="label-mono" style={{ color: "var(--stakes-primary)" }}>ADMIN CONTROL PANEL — DRAFT NEW LEAGUE</div>
              <h1 className="font-anton text-3xl sm:text-5xl lg:text-6xl uppercase leading-[0.9] mt-2 break-words">
                Configure<br/>the<br/>Stakes.
              </h1>
              <p className="font-barlow text-sm sm:text-lg mt-4 sm:mt-6 text-[var(--stakes-muted)] max-w-md">
                Name the league. Set the prize. Define who&apos;s in. Once you hit LOCK,
                the rules are sealed for the month.
              </p>
              <img src={TROPHY} alt="Trophy" className="mt-10 max-h-56 object-contain" />
            </div>

            <div className="lg:col-span-7 space-y-6 animate-slide-up" style={{ animationDelay: "120ms" }}>
              <Field label="LEAGUE NAME" testId="league-name-input">
                <input
                  value={leagueName}
                  onChange={(e) => setLeagueName(e.target.value)}
                  placeholder="e.g. THE HOUSEHOLD CUP"
                  className="w-full bg-transparent border-b-2 border-[var(--stakes-border)] focus:border-[var(--stakes-primary)] outline-none font-anton text-4xl uppercase pb-2"
                  data-testid="input-league-name"
                />
              </Field>
              <Field label="LEAGUE ADMIN" testId="admin-name-input">
                <input
                  value={adminName}
                  onChange={(e) => setAdminName(e.target.value)}
                  placeholder="e.g. MOM"
                  className="w-full bg-transparent border-b-2 border-[var(--stakes-border)] focus:border-[var(--stakes-primary)] outline-none font-anton text-4xl uppercase pb-2"
                  data-testid="input-admin-name"
                />
              </Field>
              <Field label="MONTHLY PRIZE" testId="prize-input">
                <input
                  value={prize}
                  onChange={(e) => setPrize(e.target.value)}
                  placeholder="e.g. $100 + BRAGGING RIGHTS"
                  className="w-full bg-transparent border-b-2 border-[var(--stakes-border)] focus:border-[var(--stakes-primary)] outline-none font-barlow text-2xl uppercase pb-2"
                  data-testid="input-prize"
                />
              </Field>
              <Field label="ADMIN 4-DIGIT PIN (REQUIRED)" testId="admin-pin-field">
                <input
                  value={adminPin}
                  type="password"
                  onChange={(e) => setAdminPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder="----"
                  inputMode="numeric"
                  maxLength={4}
                  autoComplete="new-password"
                  className="w-full bg-transparent border-b-2 border-[var(--stakes-border)] focus:border-[var(--neon-blue)] outline-none font-anton text-4xl tracking-[0.4em] pb-2"
                  data-testid="input-admin-pin"
                />
              </Field>

              <button
                onClick={createLeague}
                className="w-full h-16 bg-white text-black font-barlow font-black text-2xl uppercase tracking-wider hover:bg-[var(--neon-blue)] hover:text-black hover-flip"
                data-testid="create-league-button"
              >
                Draft League →
              </button>
            </div>
          </section>
        ) : (
          /* ---------- ADMIN CONTROL PANEL (existing league) ---------- */
          <section className="space-y-10">
            {/* Page identity */}
            <div data-testid="admin-control-panel-header">
              <div className="label-mono" style={{ color: "var(--stakes-admin)" }}>ADMIN CONTROL PANEL</div>
              <h1 className="font-anton text-3xl sm:text-5xl md:text-6xl uppercase leading-[0.9] mt-2 break-words">
                {locked ? "Mid-League Override" : "Full Configuration"}
              </h1>
            </div>

            {/* V2 — Legacy-league recovery email migration. Only renders
                 when this league has no REAL owner_email on file (the Phase
                 16 placeholder `admin@stakesleague.com` counts as null).
                 Once a real email is set, the card permanently disappears. */}
            {!hasRealOwnerEmail && (
              <div
                className="border-4 border-[var(--stakes-admin)] bg-[var(--stakes-surface-2)] p-5 md:p-6"
                style={{ boxShadow: "0 0 32px rgba(255,214,10,0.18), inset 0 0 60px rgba(255,214,10,0.04)" }}
                data-testid="set-recovery-email-card"
              >
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div>
                    <div className="label-mono" style={{ color: "var(--stakes-admin)" }}>
                      ⚠ LEGACY LEAGUE · ACTION REQUIRED
                    </div>
                    <div className="font-anton text-2xl sm:text-3xl uppercase leading-none mt-1">
                      Set Recovery Email
                    </div>
                  </div>
                  <div className="label-mono text-[var(--stakes-muted)]">ONE-TIME · CANNOT BE CHANGED</div>
                </div>
                <p className="font-barlow text-base uppercase text-[var(--stakes-muted)] mt-3 max-w-2xl">
                  This league has no recovery email on file. Without it the
                  <span className="text-white"> Forgot PIN? </span>
                  flow is permanently disabled. Attach one now — we&apos;ll email a verification code to confirm ownership.
                </p>
                <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-end">
                  <div className="flex-1 min-w-0">
                    <div className="label-mono">RECOVERY EMAIL</div>
                    <input
                      type="email"
                      value={recoveryEmailInput}
                      onChange={(e) => setRecoveryEmailInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && startSetRecoveryEmailFlow(recoveryEmailInput)}
                      placeholder="admin@yourdomain.com"
                      className="w-full bg-transparent border-b-2 border-[var(--stakes-border)] focus:border-[var(--stakes-admin)] outline-none font-mono uppercase text-xl pb-2"
                      data-testid="recovery-email-input"
                      autoComplete="email"
                    />
                  </div>
                  <button
                    onClick={() => startSetRecoveryEmailFlow(recoveryEmailInput)}
                    disabled={!recoveryEmailInput.trim()}
                    className="h-12 px-5 bg-[var(--stakes-admin)] !text-black font-mono font-black uppercase tracking-wider border-2 border-[var(--stakes-admin)] hover:bg-black hover:!text-white hover:border-white hover-flip disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                    data-testid="recovery-email-send-button"
                  >
                    SEND CODE →
                  </button>
                </div>
              </div>
            )}

            {/* League code banner */}
            {league.code && (
              <div
                className="border-2 p-4 md:p-5 flex items-center justify-between gap-4 flex-wrap"
                style={{
                  borderImage: "linear-gradient(90deg, var(--neon-blue), var(--neon-pink)) 1",
                  background: "linear-gradient(90deg, rgba(0,229,255,0.06), rgba(255,20,147,0.06))",
                }}
                data-testid="setup-code-banner"
              >
                <div>
                  <div className="label-mono" style={{ color: "var(--neon-blue)" }}>SHARE THIS CODE TO INVITE FAMILY</div>
                  <div className="font-anton text-3xl sm:text-5xl md:text-6xl tracking-[0.3em] leading-none mt-1">
                    #{league.code}
                  </div>
                </div>
                <button
                  onClick={() => { navigator.clipboard.writeText(league.code); toast.success("Code copied"); }}
                  className="label-mono px-4 h-12 btn-neon-blue"
                  data-testid="setup-copy-code"
                >
                  COPY CODE
                </button>
              </div>
            )}
            {/* League meta */}
            <div className="border-t border-[var(--stakes-border)] pt-8" data-testid="section-01-core-setup">
              <div className="label-mono" style={{ color: locked ? "var(--stakes-primary)" : "var(--neon-blue)" }}>
                SECTION 01 · CORE LEAGUE SETUP {locked && <span>· LOCKED FOR THE MONTH</span>}
              </div>
              <div className="grid lg:grid-cols-12 gap-8 items-end mt-4">
              <div className="lg:col-span-7">
                <div className="label-mono" style={{ color: locked ? "var(--stakes-primary)" : "var(--stakes-muted)" }}>
                  {locked ? "BASELINE RULES & PRIZE — READ-ONLY" : "BASELINE RULES & PRIZE"}
                </div>
                <input
                  value={leagueName}
                  disabled={locked}
                  onChange={(e) => setLeagueName(e.target.value)}
                  onBlur={saveLeagueMeta}
                  className="w-full bg-transparent border-0 outline-none font-anton text-2xl sm:text-4xl lg:text-6xl uppercase mt-2 disabled:opacity-100 break-words"
                  data-testid="edit-league-name"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mt-4">
                  <div>
                    <div className="label-mono">ADMIN</div>
                    <input
                      value={adminName}
                      disabled={locked}
                      onChange={(e) => setAdminName(e.target.value)}
                      onBlur={saveLeagueMeta}
                      className="w-full bg-transparent border-b border-[var(--stakes-border)] focus:border-white outline-none font-barlow text-xl sm:text-2xl uppercase pb-1 break-words"
                      data-testid="edit-admin-name"
                    />
                  </div>
                  <div className="min-w-0">
                    <div className="label-mono">PRIZE</div>
                    {locked ? (
                      <div
                        className="w-full bg-transparent border-b border-[var(--stakes-border)] font-barlow text-xl sm:text-2xl uppercase pb-1 break-words whitespace-normal wrap-words"
                        data-testid="edit-prize"
                      >
                        {prize || "—"}
                      </div>
                    ) : (
                      <input
                        value={prize}
                        onChange={(e) => setPrize(e.target.value)}
                        onBlur={saveLeagueMeta}
                        className="w-full bg-transparent border-b border-[var(--stakes-border)] focus:border-white outline-none font-barlow text-xl sm:text-2xl uppercase pb-1"
                        data-testid="edit-prize"
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="border border-[var(--stakes-border)] p-6 flex items-center gap-6">
                  <img src={TROPHY} alt="Prize" className="h-24 object-contain" />
                  <div>
                    <div className="label-mono">PLAYERS LOCKED IN</div>
                    <div className="font-anton text-4xl sm:text-5xl md:text-6xl leading-none">{users.length}</div>
                    <div className="label-mono mt-2">
                      {locked ? "ROSTER FROZEN" : "ADD AS MANY AS YOU NEED"}
                    </div>
                  </div>
                </div>
              </div>
              </div>

              {/* ============ TIMEZONE CALIBRATION (mid-league override allowed) ============ */}
              <TimezoneCard
                league={league}
                onUseDevice={() => saveTimezone(detectDeviceTimezone())}
                onPick={saveTimezone}
                detectDeviceTimezone={detectDeviceTimezone}
              />
            </div>

            {/* ============= SECTION 02 · USER ACCOUNT MANAGEMENT ============= */}
            <div className="border-t border-[var(--stakes-border)] pt-8" data-testid="section-02-user-management">
              <div className="label-mono" style={{ color: "var(--neon-blue)" }}>
                SECTION 02 · USER ACCOUNT MANAGEMENT · ALWAYS UNLOCKED
              </div>
              <h2 className="font-anton text-2xl sm:text-3xl uppercase leading-none mt-2">
                Roster &amp; PIN Overrides
              </h2>
              <p className="font-barlow text-sm sm:text-base uppercase text-[var(--stakes-muted)] mt-2">
                Add, rename, or reset PINs for any player at any time — even mid-league.
              </p>

              <div className="mt-6 space-y-4">

            {/* Add player */}
            {!locked && (
              <div className="border border-[var(--stakes-border)] bg-[var(--stakes-surface-2)] p-5 flex flex-wrap gap-4 items-end" data-testid="add-user-form">
                <div className="flex-1 min-w-[200px]">
                  <div className="label-mono">ADD PLAYER</div>
                  <input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addUser()}
                    placeholder="Name"
                    className="w-full bg-transparent border-b-2 border-[var(--stakes-border)] focus:border-white outline-none font-barlow text-2xl uppercase pb-1"
                    data-testid="new-user-name-input"
                  />
                </div>
                <div>
                  <div className="label-mono">ROLE</div>
                  <select
                    value={newType}
                    onChange={(e) => setNewType(e.target.value)}
                    className="bg-[var(--stakes-surface)] border border-[var(--stakes-border)] h-11 px-3 font-barlow uppercase text-lg"
                    data-testid="new-user-role-select"
                  >
                    <option value="admin">ADMIN</option>
                    <option value="member">MEMBER</option>
                    <option value="child">CHILD</option>
                  </select>
                </div>
                <div>
                  <div className="label-mono" style={{ color: "var(--neon-blue)" }}>PIN (OPTIONAL — BLANK = SELF-CLAIM)</div>
                  <input
                    value={newPin}
                    type="password"
                    onChange={(e) => setNewPin(e.target.value.replace(/\D/g,"").slice(0,4))}
                    placeholder="----"
                    inputMode="numeric"
                    maxLength={4}
                    autoComplete="new-password"
                    className="w-24 bg-[var(--stakes-surface)] border border-[var(--neon-blue)] h-11 px-3 font-mono-st text-xl text-center tracking-[0.4em]"
                    data-testid="new-user-pin-input"
                  />
                </div>
                <button
                  onClick={addUser}
                  className="h-11 px-5 bg-white text-black font-barlow font-black uppercase tracking-wider hover:bg-[var(--stakes-primary)] hover:text-white hover-flip"
                  data-testid="add-user-button"
                >
                  + ADD
                </button>
              </div>
            )}

            {/* Users */}
            <div className="space-y-4">
              {users.map((u) => (
                <UserEditor
                  key={u.id}
                  user={u}
                  locked={locked}
                  onUserUpdate={onUserUpdate}
                  onUserDelete={onUserDelete}
                />
              ))}
              {users.length === 0 && (
                <div className="text-center p-10 border border-dashed border-[var(--stakes-border)] label-mono">
                  NO PLAYERS YET — ADD ABOVE
                </div>
              )}
            </div>
              </div>
            </div>

            {/* ============= SECTION 03 · SYSTEM PROTOCOLS =====
                Phase 20.10 — Both passive automations explained in one block.
                Sentence-case body for readability; uppercase headers + section
                stamp preserved for the Tactical-Brutalism aesthetic. */}
            <div className="border-t border-[var(--stakes-border)] pt-8" data-testid="section-03-protocols">
              <div className="label-mono" style={{ color: "var(--neon-pink)" }}>
                [ SECTION 03 · SYSTEM PROTOCOLS ]
              </div>
              <h2 className="font-anton text-2xl sm:text-3xl uppercase leading-none mt-2">
                Automated Directives
              </h2>

              {/* Weekend Redemption */}
              <div className="mt-5 sm:mt-6 border border-[var(--stakes-border)] bg-black/30 p-4 sm:p-5">
                <div className="label-mono" style={{ color: "var(--stakes-primary)" }}>
                  WEEKEND REDEMPTION
                </div>
                <p className="font-barlow text-sm sm:text-base text-white/85 mt-2 leading-relaxed">
                  Fires automatically every Friday at 18:00 (IST). Any operative trailing the Leader is assigned a randomized penalty directive. Deadline: Sunday 23:59 (IST).
                </p>
              </div>

              {/* Rogue Directive */}
              <div className="mt-3 border border-[var(--stakes-border)] bg-black/30 p-4 sm:p-5">
                <div className="label-mono" style={{ color: "var(--neon-blue)" }}>
                  ROGUE DIRECTIVE
                </div>
                <p className="font-barlow text-sm sm:text-base text-white/85 mt-2 leading-relaxed">
                  A randomized tactical strike. Deploys unannounced on a weekday. The last-place operative is assigned a high-value task to serve the current Leader. 24-hour expiration.
                </p>
                {rogueState && (
                  <div
                    className="label-mono mt-3"
                    style={{ color: ROGUE_STATE_COLOR[rogueState] || "var(--stakes-muted)" }}
                    data-testid="rogue-status-line"
                  >
                    [ {ROGUE_STATE_LABEL[rogueState] || rogueState.toUpperCase()} ]
                  </div>
                )}
              </div>
            </div>

            {/* Lock CTA */}
            {!locked && (
              <div className="border-t border-[var(--stakes-border)] pt-10">
                <div className="label-mono" style={{ color: "var(--stakes-primary)" }}>POINT OF NO RETURN</div>
                <button
                  onClick={() => setShowLock(true)}
                  disabled={users.length === 0}
                  className="mt-4 w-full h-16 sm:h-20 lg:h-24 bg-[var(--stakes-primary)] text-white font-anton text-xl sm:text-3xl lg:text-5xl uppercase tracking-tight hover:bg-[var(--stakes-primary-hover)] disabled:opacity-30 disabled:cursor-not-allowed animate-pulse-red hover-flip"
                  data-testid="start-league-button"
                >
                  START LEAGUE. LOCK RULES.
                </button>
                <div className="mt-2 label-mono text-center">
                  RULES BECOME READ-ONLY FOR EVERY PLAYER FOR THE ENTIRE MONTH
                </div>
              </div>
            )}

            {/* Phase 20.16 — Status toggle. Draft ↔ Active. Draft mode
                also unlocks task_settings so admin can edit rules. */}
            {league?.id && (
              <div
                className="mt-10 pt-8 border-t border-[var(--stakes-border)] flex items-center justify-between gap-3"
                data-testid="league-status-toggle-row"
              >
                <div className="min-w-0">
                  <div className="label-mono">LEAGUE STATUS</div>
                  <div
                    className="font-anton text-2xl uppercase mt-1 leading-none"
                    data-testid="league-status-value"
                  >
                    {league.status === "active" ? "ACTIVE · LOCKED" : "DRAFT · EDITABLE"}
                  </div>
                </div>
                <button
                  onClick={toggleStatus}
                  className="label-mono px-4 h-11 border border-white hover:bg-white hover:text-black hover-flip flex-shrink-0"
                  data-testid="league-status-toggle-button"
                >
                  {league.status === "active"
                    ? "[ SWITCH TO DRAFT MODE ]"
                    : "[ SWITCH TO ACTIVE MODE ]"}
                </button>
              </div>
            )}

            {/* Phase 20.15 — Admin Purge Utility (only rendered inside
                the verified admin gate). Sits directly above DANGER ZONE
                so surgical operations are visually adjacent to WIPE. */}
            {league?.id && <AdminPurgeUtility leagueId={league.id} />}

            {/* Reset (always visible) */}
            <div className="flex justify-between items-center pt-10 border-t border-[var(--stakes-border)]">
              <div className="label-mono">DANGER ZONE</div>
              <button
                onClick={resetAll}
                className="label-mono px-4 h-10 border border-[var(--stakes-border)] hover:border-[var(--stakes-primary)] hover:text-[var(--stakes-primary)] hover-flip"
                data-testid="reset-league-button"
              >
                WIPE LEAGUE
              </button>
            </div>
          </section>
        )}

        {/* Phase 20.11 — System build tag. Lives at the absolute bottom of the
            scrollable area so it surfaces below DANGER ZONE / Protocols / any
            other section. Muted, centered, monospace, brutalist. */}
        <div
          className="text-xs font-mono text-neutral-600 tracking-[0.2em] text-center mt-12 pb-6"
          data-testid="system-build-tag"
        >
          SYSTEM BUILD: v1.1.0
        </div>
      </main>
      </div>

      <LockConfirmModal
        open={showLock}
        league={league}
        userCount={users.length}
        onClose={() => setShowLock(false)}
        onConfirm={startLeague}
      />

      {/* V2 — OTP gate for league creation + admin PIN recovery + legacy recovery-email migration */}
      <OTPModal
        open={otpOpen}
        onClose={() => setOtpOpen(false)}
        onVerified={handleOtpVerified}
        title={
          otpMode === "create"
            ? "VERIFY ADMIN EMAIL"
            : otpMode === "set-recovery"
              ? "ATTACH RECOVERY EMAIL"
              : "ADMIN PIN RECOVERY"
        }
        subtitle={
          otpMode === "create"
            ? "THIS EMAIL OWNS THE LEAGUE AND IS USED FOR PIN RECOVERY."
            : otpMode === "set-recovery"
              ? `A 6-DIGIT CODE WILL BE EMAILED TO ${(otpPendingEmail || "").toUpperCase()}.`
              : `A 6-DIGIT CODE WILL BE EMAILED TO ${(realOwnerEmail || "").toUpperCase()}.`
        }
        lockedEmail={otpLockedEmail}
        ctaSendLabel={otpMode === "create" ? "SEND CODE" : "EMAIL CODE"}
        ctaVerifyLabel={
          otpMode === "create"
            ? "VERIFY & CREATE →"
            : otpMode === "set-recovery"
              ? "VERIFY & ATTACH →"
              : "VERIFY →"
        }
      />
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <div className="label-mono mb-1">{label}</div>
      {children}
    </label>
  );
}

// ============= REDEMPTION MANAGEMENT =============
// Renders below the user list. Stays 100% editable mid-league (no `locked` gate).
// Lists every user with point deficit >= 50 vs. the league leader
// and lets the verified admin commit a specific penalty task per user.
function RedemptionManagement({ league, users, adminActorId }) {
  const [rows, setRows] = useState([]); // [{ user, info, draft, busy }]
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!league || !users.length) { setRows([]); setLoading(false); return; }
    setLoading(true);
    try {
      const results = await Promise.all(
        users.map((u) => api.getRedemption(u.id, league.id).catch(() => null))
      );
      // Spec: qualify only when point deficit >= 50 compared to the leader.
      const next = results
        .map((info, i) => (info && info.gap >= 50 ? { user: users[i], info, draft: info.task || "", busy: false } : null))
        .filter(Boolean);
      setRows(next);
    } finally {
      setLoading(false);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    load();
  }, [league?.id, users.map((u) => u.id).join(",")]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const updateDraft = (userId, value) => {
    setRows((rs) => rs.map((r) => (r.user.id === userId ? { ...r, draft: value } : r)));
  };

  const commitPenalty = async (row) => {
    if (!row.draft.trim()) {
      toast.error("Penalty text required");
      return;
    }
    if (!adminActorId) {
      toast.error("Admin actor not verified");
      return;
    }
    setRows((rs) => rs.map((r) => (r.user.id === row.user.id ? { ...r, busy: true } : r)));
    try {
      const updated = await api.setRedemption(row.user.id, league.id, row.draft.trim(), adminActorId);
      setRows((rs) =>
        rs.map((r) => (r.user.id === row.user.id ? { ...r, info: updated, draft: updated.task || "", busy: false } : r))
      );
      toast.success(`Penalty committed for ${row.user.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't commit penalty");
      setRows((rs) => rs.map((r) => (r.user.id === row.user.id ? { ...r, busy: false } : r)));
    }
  };

  const suggestFor = async (userId) => {
    try {
      const s = await api.suggestTask();
      updateDraft(userId, s.task);
    } catch { /* ignore */ }
  };

  return (
    <section
      className="border-2 border-[var(--neon-pink)] bg-[var(--stakes-surface)] p-5 md:p-6"
      style={{ boxShadow: "0 0 24px rgba(255,20,147,0.18), inset 0 0 40px rgba(255,20,147,0.05)" }}
      data-testid="redemption-management-section"
    >
      <div className="label-mono" style={{ color: "var(--neon-pink)" }}>
        ALWAYS ACTIVE • OVERRIDES LEAGUE LOCK
      </div>
      <h2 className="font-anton text-3xl md:text-4xl uppercase leading-none mt-1">
        Redemption Management
      </h2>
      <p className="font-barlow text-base uppercase text-[var(--stakes-muted)] mt-2">
        Lists every player with a deficit of 50+ points behind the league leader.
        Commits are admin-only and audit-logged.
      </p>

      {loading ? (
        <div className="mt-6 label-mono">SCANNING DEFICITS…</div>
      ) : rows.length === 0 ? (
        <div
          className="mt-6 border border-dashed border-[var(--stakes-border)] p-6 text-center label-mono"
          data-testid="redemption-mgmt-empty"
        >
          NO PLAYERS CURRENTLY QUALIFY FOR REDEMPTION.
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {rows.map((row) => {
            const reasonLabel = `DEFICIT · ${row.info.gap} PTS BEHIND LEADER`;
            const color = ACCOUNT_COLORS[row.user.account_type] || "#fff";
            return (
              <div
                key={row.user.id}
                className="border border-[var(--stakes-border)] bg-[var(--stakes-surface-2)] p-4"
                data-testid={`redemption-mgmt-row-${row.user.id}`}
              >
                <div className="flex items-end justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="label-mono" style={{ color }}>{reasonLabel}</div>
                    <div className="font-anton text-3xl uppercase leading-none mt-1 wrap-words" data-testid={`redemption-mgmt-name-${row.user.id}`}>
                      {row.user.name}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="label-mono">BOUNTY</div>
                    <div className="font-anton text-3xl leading-none" style={{ color: "var(--neon-pink)" }} data-testid={`redemption-mgmt-bounty-${row.user.id}`}>
                      +{row.info.bounty}
                    </div>
                  </div>
                </div>

                {row.info.task && (
                  <div className="mt-3 label-mono">
                    CURRENT PENALTY · <span style={{ color: row.info.completed ? "var(--stakes-child)" : "var(--neon-pink)" }}>
                      {row.info.completed ? "COMPLETED" : "PENDING"}
                    </span>
                  </div>
                )}

                <div className="mt-3">
                  <textarea
                    value={row.draft}
                    onChange={(e) => updateDraft(row.user.id, e.target.value)}
                    rows={2}
                    placeholder="Type a high-effort penalty task…"
                    className="w-full bg-transparent border border-[var(--stakes-border)] focus:border-white outline-none p-3 font-barlow text-lg uppercase wrap-words"
                    data-testid={`redemption-mgmt-input-${row.user.id}`}
                  />
                </div>

                <div className="mt-3 flex gap-2 flex-wrap">
                  <button
                    onClick={() => suggestFor(row.user.id)}
                    disabled={row.busy}
                    className="px-3 h-10 bg-white !text-black font-mono uppercase tracking-wider text-xs font-bold border-2 border-white hover:bg-black hover:!text-white hover:border-white hover-flip"
                    data-testid={`redemption-mgmt-suggest-${row.user.id}`}
                  >
                    ✨ SUGGEST
                  </button>
                  <button
                    onClick={() => commitPenalty(row)}
                    disabled={row.busy || !row.draft.trim()}
                    className="px-4 h-10 bg-[var(--neon-pink)] !text-black font-mono uppercase tracking-wider text-sm font-black border-2 border-[var(--neon-pink)] hover:bg-black hover:!text-white hover:border-white hover-flip ml-auto disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`redemption-mgmt-commit-${row.user.id}`}
                  >
                    {row.busy ? "COMMITTING…" : "COMMIT PENALTY"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}


// ============================================================================
// TIMEZONE CALIBRATION CARD (Phase 17)
// ----------------------------------------------------------------------------
// Allowed to be edited even when the league is `active` — see server.py
// `update_league`. This is the one-and-only baseline that drives:
//   • Saturday-noon hard freeze
//   • Weekly crown sweep (Sun 23:00 local)
//   • Monthly trophy sweep (last day 23:00 local)
// ============================================================================
const COMMON_TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Australia/Sydney",
  "Pacific/Auckland",
];

function TimezoneCard({ league, onUseDevice, onPick, detectDeviceTimezone }) {
  const current = league?.timezone || "UTC";
  const device = detectDeviceTimezone();
  const matchesDevice = current === device;

  // Local-time preview so admin can confirm "yes, that's our actual wall clock"
  const [nowLabel, setNowLabel] = useState("");
  useEffect(() => {
    const tick = () => {
      try {
        const fmt = new Intl.DateTimeFormat("en-US", {
          timeZone: current,
          weekday: "short",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });
        setNowLabel(fmt.format(new Date()).toUpperCase());
      } catch {
        setNowLabel("INVALID TZ");
      }
    };
    tick();
    const t = setInterval(tick, 30 * 1000);
    return () => clearInterval(t);
  }, [current]);

  const optionList = COMMON_TIMEZONES.includes(current)
    ? COMMON_TIMEZONES
    : [current, ...COMMON_TIMEZONES];

  return (
    <div
      className="mt-8 border-2 border-[var(--stakes-border)] bg-[var(--stakes-surface-2)] p-5 sm:p-6"
      data-testid="timezone-card"
    >
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mono" style={{ color: "var(--neon-blue)" }}>
            TIMEZONE CALIBRATION · ALWAYS UNLOCKED
          </div>
          <div className="font-anton text-2xl sm:text-3xl uppercase leading-none mt-1">
            System Wall Clock
          </div>
        </div>
        <div className="label-mono text-right" style={{ color: "var(--stakes-muted)" }} data-testid="timezone-now-preview">
          LEAGUE TIME · {nowLabel || "—"}
        </div>
      </div>

      <p className="font-barlow text-base uppercase text-[var(--stakes-muted)] mt-3 max-w-2xl">
        All deadlines — Saturday-noon freeze, weekly crown, monthly trophy — fire on this clock. Set it to the family&apos;s actual local zone.
      </p>

      <div className="mt-5 grid sm:grid-cols-[1fr_auto] gap-3 items-center">
        <div className="border border-[var(--stakes-border)] px-4 py-3 bg-black">
          <div className="label-mono" style={{ color: "var(--stakes-muted)" }}>ACTIVE</div>
          <div
            className="font-mono text-xl sm:text-2xl uppercase tracking-wider mt-1 break-all"
            data-testid="timezone-active-label"
          >
            {current}
          </div>
        </div>
        <button
          onClick={onUseDevice}
          disabled={matchesDevice}
          className="px-5 h-12 bg-[var(--neon-blue)] !text-black font-mono uppercase tracking-wider text-sm font-black border-2 border-[var(--neon-blue)] hover:bg-black hover:!text-white hover:border-white hover-flip disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="timezone-use-device-button"
          title={matchesDevice ? "Already matches this device" : `Set to ${device}`}
        >
          {matchesDevice ? "✓ MATCHES DEVICE" : "USE DEVICE TIMEZONE"}
        </button>
      </div>

      <div className="mt-4">
        <div className="label-mono">OR PICK MANUALLY</div>
        <select
          value={current}
          onChange={(e) => onPick(e.target.value)}
          className="mt-1 w-full sm:w-auto bg-[var(--stakes-surface)] border-2 border-[var(--stakes-border)] focus:border-white outline-none h-11 px-3 font-mono uppercase text-base"
          data-testid="timezone-manual-select"
        >
          {optionList.map((tz) => (
            <option key={tz} value={tz}>{tz}</option>
          ))}
        </select>
        <div className="label-mono mt-2" style={{ color: "var(--stakes-muted)" }}>
          DEVICE DETECTED · {device}
        </div>
      </div>
    </div>
  );
}
